import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ProtectedEmail, ProtectedPhone } from '@/components/ProtectedContact';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white py-16" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Informativa sulla Privacy
          </h1>
          <p className="text-slate-500 mb-8">
            Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}
          </p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                1. Titolare del Trattamento
              </h2>
              <p className="text-slate-600 mb-4">
                Il Titolare del trattamento dei dati è:
              </p>
              <div className="bg-slate-50 p-4 rounded-lg mb-4">
                <p className="text-slate-700">
                  <strong>Studio Professionale Azzano Cantarutti SRL STP</strong><br />
                  Dott. Aurelio Azzano Cantarutti<br />
                  Piazza Pellegrino 8<br />
                  33038 San Daniele del Friuli (UD)<br />
                  Email: <ProtectedEmail user="info" domain="studioazzanostp.it" className="text-indigo-600 hover:underline" /><br />
                  Tel: <ProtectedPhone parts={['+39', '0427', '40326']} display="+39 0427 40326" className="text-indigo-600 hover:underline" /><br />
                  P.IVA: IT03196730307<br />
                  C.F.: 03196730307
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                2. Tipologie di Dati Raccolti
              </h2>
              <p className="text-slate-600 mb-4">
                I dati personali raccolti dal Titolare, in modo autonomo o tramite terze parti, includono:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Dati identificativi:</strong> nome, cognome, ragione sociale</li>
                <li><strong>Dati di contatto:</strong> indirizzo email, numero di telefono</li>
                <li><strong>Dati di navigazione:</strong> indirizzo IP, browser utilizzato, pagine visitate</li>
                <li><strong>Dati forniti volontariamente:</strong> informazioni inserite nei form di contatto e prenotazione</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                3. Finalità del Trattamento
              </h2>
              <p className="text-slate-600 mb-4">
                I dati personali sono trattati per le seguenti finalità:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Erogazione del servizio:</strong> gestione delle richieste di consulenza e prenotazioni</li>
                <li><strong>Comunicazioni:</strong> rispondere alle richieste di informazioni</li>
                <li><strong>Adempimenti legali:</strong> adempiere agli obblighi previsti dalla legge</li>
                <li><strong>Miglioramento del servizio:</strong> analisi statistiche anonime per migliorare il sito</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                4. Base Giuridica del Trattamento
              </h2>
              <p className="text-slate-600 mb-4">
                Il trattamento dei dati personali si basa su:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Consenso dell'interessato</strong> (Art. 6, par. 1, lett. a) GDPR)</li>
                <li><strong>Esecuzione di un contratto</strong> (Art. 6, par. 1, lett. b) GDPR)</li>
                <li><strong>Adempimento di obblighi legali</strong> (Art. 6, par. 1, lett. c) GDPR)</li>
                <li><strong>Legittimo interesse</strong> del Titolare (Art. 6, par. 1, lett. f) GDPR)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                5. Modalità di Trattamento
              </h2>
              <p className="text-slate-600 mb-4">
                Il trattamento dei dati personali è effettuato mediante strumenti informatici e/o telematici, 
                con logiche strettamente correlate alle finalità indicate e, comunque, in modo da garantire 
                la sicurezza e la riservatezza dei dati stessi.
              </p>
              <p className="text-slate-600">
                Sono adottate misure di sicurezza tecniche e organizzative adeguate per proteggere i dati 
                da accessi non autorizzati, perdita, distruzione o divulgazione.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                6. Periodo di Conservazione
              </h2>
              <p className="text-slate-600 mb-4">
                I dati personali sono conservati per il tempo necessario al conseguimento delle finalità 
                per cui sono stati raccolti e comunque:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Dati contabili e fiscali: 10 anni (obblighi di legge)</li>
                <li>Dati di contatto: fino alla revoca del consenso</li>
                <li>Dati di navigazione: 24 mesi</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                7. Comunicazione e Diffusione dei Dati
              </h2>
              <p className="text-slate-600 mb-4">
                I dati personali potranno essere comunicati a:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Collaboratori e dipendenti del Titolare</li>
                <li>Fornitori di servizi tecnici (hosting, email)</li>
                <li>Autorità competenti, se richiesto dalla legge</li>
              </ul>
              <p className="text-slate-600 mt-4">
                I dati non saranno in alcun modo diffusi a terzi non autorizzati.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                8. Trasferimento dei Dati
              </h2>
              <p className="text-slate-600">
                I dati personali sono trattati presso la sede del Titolare e in ogni altro luogo in cui 
                le parti coinvolte nel trattamento siano localizzate. Per ulteriori informazioni, 
                contattare il Titolare. I dati potrebbero essere trasferiti in paesi extra-UE solo 
                in presenza di adeguate garanzie (es. Privacy Shield, clausole contrattuali standard).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                9. Diritti dell'Interessato
              </h2>
              <p className="text-slate-600 mb-4">
                Ai sensi degli articoli 15-22 del GDPR, l'interessato ha diritto di:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Accesso:</strong> ottenere conferma del trattamento e accedere ai propri dati</li>
                <li><strong>Rettifica:</strong> ottenere la correzione di dati inesatti</li>
                <li><strong>Cancellazione:</strong> ottenere la cancellazione dei dati ("diritto all'oblio")</li>
                <li><strong>Limitazione:</strong> ottenere la limitazione del trattamento</li>
                <li><strong>Portabilità:</strong> ricevere i dati in formato strutturato</li>
                <li><strong>Opposizione:</strong> opporsi al trattamento per motivi legittimi</li>
                <li><strong>Revoca del consenso:</strong> revocare il consenso in qualsiasi momento</li>
              </ul>
              <p className="text-slate-600 mt-4">
                Per esercitare i propri diritti, l'interessato può inviare una richiesta a: 
                <ProtectedEmail user="info" domain="studioazzanostp.it" className="text-indigo-600 hover:underline ml-1" />
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                10. Reclamo all'Autorità di Controllo
              </h2>
              <p className="text-slate-600">
                L'interessato ha il diritto di proporre reclamo all'Autorità Garante per la Protezione 
                dei Dati Personali (www.garanteprivacy.it) qualora ritenga che il trattamento dei propri 
                dati personali violi il GDPR.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                11. Modifiche alla Privacy Policy
              </h2>
              <p className="text-slate-600">
                Il Titolare si riserva il diritto di apportare modifiche alla presente informativa. 
                Le modifiche saranno pubblicate su questa pagina con indicazione della data di 
                ultimo aggiornamento. Si consiglia di consultare periodicamente questa pagina.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
